import fallbackImage from "@assets/images/fallback-game.svg";
import {useGetRecentGames} from "@hooks/gamesList/useGetRecentGames";
import {IconHeart, IconHeartFilled, IconPlayerPlay} from "@tabler/icons-react";
import {useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ImageWithLoadSpinner} from "@/components/image-with-load-spinner";
import useAddFavoriteGame from "@/hooks/games/useAddFavoriteGame.hook";
import useDeleteFavoriteGame from "@/hooks/games/useDeleteFavoriteGame.hook";
import {useAuthStore} from "@/store/useAuth.store";
import {useMiniGames} from "@/store/useMiniGames";
import {Carousel} from "../../../../../../../components/Carousel";
import {HeadingCarousel} from "../../../../../../../components/heading-carousel";
import styles from "./recently-played-games.styles.module.scss";

export const RecentlyPlayedGames = () => {
  const {t} = useTranslation();
  const {recentGames, isPending} = useGetRecentGames();
  const [carouselApi, setCarouselApi] = useState(null);
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const setFavoriteGames = useMiniGames(state => state.setFavoriteGames);
  const storeFavoriteGames = useMiniGames(state => state.favoriteGames);
  const {addFavoriteGame} = useAddFavoriteGame();
  const {deleteFavoriteGame} = useDeleteFavoriteGame();

  const isGameFavorited = useCallback(
    gameId => storeFavoriteGames.some(f => f.game_id === gameId),
    [storeFavoriteGames]
  );

  const handleFavorite = useCallback(
    (e, game) => {
      e.stopPropagation();
      setFavoriteGames(
        {id: game.game_id, provider: game.game_provider, game_provider: game.game_provider},
        addFavoriteGame,
        deleteFavoriteGame
      );
    },
    [setFavoriteGames, addFavoriteGame, deleteFavoriteGame]
  );

  const handleGameClick = useCallback(
    (provider, gameId) => {
      navigate(`/games/${provider}/${gameId}`);
    },
    [navigate]
  );

  if (authStatus !== "authenticated" || (!isPending && recentGames.length === 0)) {
    return null;
  }

  return (
    <div
      id="recentlyPlayedGames"
      className={styles.container}
    >
      <HeadingCarousel
        title={t("recentlyPlayedTitle")}
        subtitle={t("recentlyPlayedSubTitle")}
        icon="▶️"
        onPrev={carouselApi?.scrollPrev}
        onNext={carouselApi?.scrollNext}
      />
      <Carousel
        onInit={setCarouselApi}
        renderItems={() =>
          isPending || recentGames.length === 0
            ? ["skel-1", "skel-2", "skel-3", "skel-4", "skel-5", "skel-6"].map(key => (
                <div
                  key={key}
                  className={styles.gameCard}
                >
                  <div className={styles.loadingIcon} />
                </div>
              ))
            : recentGames.map(game => (
                <div
                  key={game.uniqueId}
                  className={styles.gameCard}
                  onClick={() => handleGameClick(game.game_provider, game.game_id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === "Enter" && handleGameClick(game.game_provider, game.game_id)}
                >
                  <ImageWithLoadSpinner
                    src={game.icon || fallbackImage}
                    alt={game.name}
                    fallbackSrc={fallbackImage}
                    wrapperClassName={styles.gameIcon}
                    objectFit="cover"
                    showSpinner={false}
                  />
                  <div className={styles.gradientOverlay} />
                  <div className={styles.hoverOverlay}>
                    <div className={styles.playBtn}>
                      <IconPlayerPlay
                        size={16}
                        fill="#000"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`${styles.favBtn} ${isGameFavorited(game.game_id) ? styles.favActive : ""}`}
                    onClick={e => handleFavorite(e, game)}
                    aria-label={isGameFavorited(game.game_id) ? "Remove from favorites" : "Add to favorites"}
                  >
                    {isGameFavorited(game.game_id) ? (
                      <IconHeartFilled
                        size={13}
                        color="#ff4d6a"
                      />
                    ) : (
                      <IconHeart
                        size={13}
                        stroke={2}
                        color="#fff"
                      />
                    )}
                  </button>
                  <div className={styles.cardFooter}>
                    <span className={styles.gameName}>{game.name}</span>
                    <span className={styles.providerName}>{game.provider}</span>
                  </div>
                </div>
              ))
        }
      />
    </div>
  );
};
